import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import { FC, useCallback, useEffect, useState } from 'react'

export interface DashboardDateFilter {
mode: 'year' | 'custom'
year: number | null
sinceDate: string | null
untilDate: string | null
}

interface DashboardFilterModalProps {
open: boolean
onClose: () => void
initialFilters: DashboardDateFilter
onApplyFilter: (filters: DashboardDateFilter) => void
}

export const DashboardFilterModal: FC<DashboardFilterModalProps> = ({
open,
onClose,
initialFilters,
onApplyFilter,
}) => {
const [selectedYear, setSelectedYear] = useState<Dayjs | null>(
  initialFilters.year ? dayjs().year(initialFilters.year).startOf('year') : dayjs().startOf('year'),
)
const [useCustomRange, setUseCustomRange] = useState<boolean>(initialFilters.mode === 'custom')
const [sinceDate, setSinceDate] = useState<Dayjs | null>(
  initialFilters.sinceDate ? dayjs(initialFilters.sinceDate) : null,
)
const [untilDate, setUntilDate] = useState<Dayjs | null>(
  initialFilters.untilDate ? dayjs(initialFilters.untilDate) : null,
)
const [error, setError] = useState<string | null>(null)

useEffect(() => {
  setSelectedYear(
    initialFilters.year
      ? dayjs().year(initialFilters.year).startOf('year')
      : dayjs().startOf('year'),
  )
  setUseCustomRange(initialFilters.mode === 'custom')
  setSinceDate(initialFilters.sinceDate ? dayjs(initialFilters.sinceDate) : null)
  setUntilDate(initialFilters.untilDate ? dayjs(initialFilters.untilDate) : null)
  setError(null)
}, [initialFilters, open])

const handleApply = useCallback(() => {
  if (useCustomRange) {
    if (!sinceDate || !untilDate) {
      setError('Please select both start and end month.')
      return
    }

    if (sinceDate.isAfter(untilDate)) {
      setError('Start month cannot be after end month.')
      return
    }

    const diffMonths = untilDate.diff(sinceDate, 'month', true)

    if (diffMonths > 11) {
      setError('Month range must not exceed 12 months.')
      return
    }

    setError(null)

    onApplyFilter({
      mode: 'custom',
      year: null,
      sinceDate: sinceDate.startOf('month').format('YYYY-MM-DD'),
      untilDate: untilDate.startOf('month').format('YYYY-MM-DD'),
    })

    onClose()
    return
  }

  if (!selectedYear) {
    setError('Please select a year.')
    return
  }

  setError(null)

  onApplyFilter({
    mode: 'year',
    year: selectedYear.year(),
    sinceDate: null,
    untilDate: null,
  })

  onClose()
}, [useCustomRange, selectedYear, sinceDate, untilDate, onApplyFilter, onClose])

const handleReset = useCallback(() => {
  const defaultYear = dayjs().startOf('year')
  setSelectedYear(defaultYear)
  setUseCustomRange(false)
  setSinceDate(null)
  setUntilDate(null)
  setError(null)
  onApplyFilter({
    mode: 'year',
    year: defaultYear.year(),
    sinceDate: null,
    untilDate: null,
  })
  onClose()
}, [onApplyFilter, onClose])

return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ p: 2 }}>
        <Typography variant="h6" fontFamily="Gabarito">
          Filter By:
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <DatePicker
              label="Year"
              views={['year']}
              value={selectedYear}
              onChange={(val) => {
                setSelectedYear(val)
                setError(null)
              }}
              disabled={useCustomRange}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  error: !useCustomRange && !!error,
                },
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={useCustomRange}
                  onChange={(event) => {
                    const isChecked = event.target.checked
                    setUseCustomRange(isChecked)

                    if (isChecked && (!sinceDate || !untilDate)) {
                      const baseYear = selectedYear?.year() ?? dayjs().year()
                      setSinceDate(dayjs().year(baseYear).month(0).startOf('month'))
                      setUntilDate(dayjs().year(baseYear).month(11).startOf('month'))
                    }

                    setError(null)
                  }}
                />
              }
              label="Use custom month range"
            />

            {useCustomRange && (
              <Stack direction="row" spacing={2}>
                <DatePicker
                  label="Start (Month - Year)"
                  views={['year', 'month']}
                  value={sinceDate}
                  onChange={(val) => {
                    setSinceDate(val)
                    setError(null)
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      error: useCustomRange && !!error,
                    },
                  }}
                />
                <DatePicker
                  label="Until (Month - Year)"
                  views={['year', 'month']}
                  value={untilDate}
                  onChange={(val) => {
                    setUntilDate(val)
                    setError(null)
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      error: useCustomRange && !!error,
                      helperText: error ?? '',
                    },
                  }}
                />
              </Stack>
            )}

            {!useCustomRange && error && (
              <Typography variant="caption" color="error.main">
                {error}
              </Typography>
            )}

            {useCustomRange && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Custom mode applies specific month and year filter.
              </Typography>
            )}
            {!useCustomRange && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Default applies only a year filter.
              </Typography>
            )}
          </Stack>
        </LocalizationProvider>
      </DialogContent>

      <DialogActions sx={{ p: 2, mt: 2 }}>
        <Button
          onClick={handleReset}
          sx={{
            color: 'text.secondary',
            textTransform: 'none',
            fontFamily: 'Gabarito',
          }}
        >
          Reset Filter
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          sx={{
            bgcolor: 'secondary.main',
            textTransform: 'none',
            fontFamily: 'Gabarito',
            '&:hover': { bgcolor: 'secondary.dark' },
          }}
        >
          Apply Filter
        </Button>
      </DialogActions>
    </Dialog>
)
}